import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../../contexts/AuthContext';
import { fetchPersonDetail, updatePersonAlias, generatePrepNotes, emailPrepNotes } from '../../services/dashboardService';
import { 
  ArrowLeft, 
  User, 
  UserCheck, 
  UserCog,
  Building,
  ExternalLink,
  MessageSquare,
  Calendar,
  TrendingUp,
  Loader2,
  Pencil,
  Check,
  X,
  FileText,
  Mail,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const RELATIONSHIP_ICONS = {
  direct_report: UserCheck,
  manager: UserCog,
  peer: User,
  skip_level: Building,
  stakeholder: Building,
  external: ExternalLink
};

const RELATIONSHIP_LABELS = {
  direct_report: 'Direct Report',
  manager: 'Manager',
  peer: 'Peer',
  skip_level: 'Skip Level',
  stakeholder: 'Stakeholder',
  external: 'External'
};

const SITUATION_COLORS = {
  feedback: 'bg-blue-500/10 text-blue-400',
  delegation: 'bg-green-500/10 text-green-400',
  difficult_conversation: 'bg-red-500/10 text-red-400',
  decision_making: 'bg-purple-500/10 text-purple-400',
  one_to_one: 'bg-yellow-500/10 text-yellow-400',
  team_conflict: 'bg-orange-500/10 text-orange-400',
  general: 'bg-zinc-500/10 text-zinc-400'
};

// Validate alias format: Name.X or Name.XX
const isValidAlias = (alias) => {
  return /^[A-Za-z]+\.[A-Za-z0-9]{1,2}$/.test(alias);
};

const PersonDetailPage = () => {
  const { alias } = useParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [person, setPerson] = useState(null);
  const [reflections, setReflections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Content quality check from backend
  const [hasEnoughContent, setHasEnoughContent] = useState(false);
  const [prepNotesReason, setPrepNotesReason] = useState(null);
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editedAlias, setEditedAlias] = useState('');
  const [editError, setEditError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Prep notes state
  const [prepNotesLoading, setPrepNotesLoading] = useState(false);
  const [prepNotes, setPrepNotes] = useState(null);
  const [prepNotesError, setPrepNotesError] = useState(null);
  const [showPrepNotes, setShowPrepNotes] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    const loadPerson = async () => {
      try {
        const data = await fetchPersonDetail(alias);
        setPerson(data.person);
        setReflections(data.related_reflections || []);
        setEditedAlias(data.person.alias);
        // Use backend's content quality check
        setHasEnoughContent(data.has_enough_content_for_prep || false);
        setPrepNotesReason(data.prep_notes_reason || null);
      } catch (err) {
        if (err.message === 'Unauthorized') {
          navigate('/login', { replace: true });
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && alias) {
      loadPerson();
    }
  }, [isAuthenticated, alias, navigate]);

  const handleStartEdit = () => {
    setIsEditing(true);
    setEditedAlias(person.alias);
    setEditError(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedAlias(person.alias);
    setEditError(null);
  };

  const handleSaveAlias = async () => {
    if (!isValidAlias(editedAlias)) {
      setEditError('Format must be Name.X or Name.XX (e.g., Sarah.M)');
      return;
    }

    if (editedAlias === person.alias) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    setEditError(null);

    try {
      await updatePersonAlias(person.alias, editedAlias);
      navigate(`/dashboard/people/${encodeURIComponent(editedAlias)}`, { replace: true });
    } catch (err) {
      setEditError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAliasKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveAlias();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleGeneratePrepNotes = async () => {
    // Check if backend says we have enough content
    if (!hasEnoughContent) {
      setPrepNotesError(prepNotesReason || `Need more reflections with detailed content about ${displayName} to generate prep notes.`);
      return;
    }

    setPrepNotesLoading(true);
    setPrepNotesError(null);
    setPrepNotes(null);
    setEmailSent(false);

    try {
      const result = await generatePrepNotes(person.alias);
      
      if (result.success) {
        setPrepNotes(result.prep_notes);
        setShowPrepNotes(true);
      } else {
        setPrepNotesError(result.message || 'Unable to generate prep notes');
      }
    } catch (err) {
      setPrepNotesError(err.message || 'Failed to generate prep notes');
    } finally {
      setPrepNotesLoading(false);
    }
  };

  const handleEmailPrepNotes = async () => {
    if (!prepNotes) return;

    setEmailSending(true);

    try {
      await emailPrepNotes(person.alias, prepNotes);
      setEmailSent(true);
    } catch (err) {
      setPrepNotesError(err.message || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  if (error || !person) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <header className="border-b border-zinc-800">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link to="/dashboard/people" className="flex items-center gap-2 text-zinc-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
              Back to People
            </Link>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-16 text-center">
          <User className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Person not found</h2>
          <p className="text-zinc-400">{error || `Could not find "${alias}" in your context.`}</p>
        </main>
      </div>
    );
  }

  const IconComponent = RELATIONSHIP_ICONS[person.relationship] || User;
  const displayName = person.alias.split('.')[0];
  // Use the backend's content quality check for enabling the button
  const hasEnoughForPrep = hasEnoughContent;

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/dashboard/people" className="flex items-center gap-2 text-zinc-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
            Back to People
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Person header */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-zinc-800 rounded-xl flex items-center justify-center flex-shrink-0">
              <IconComponent className="w-8 h-8 text-zinc-400" />
            </div>
            <div className="flex-1 min-w-0">
              {/* Name with edit functionality */}
              <div className="flex items-center gap-2 mb-1">
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editedAlias}
                      onChange={(e) => setEditedAlias(e.target.value)}
                      onKeyDown={handleAliasKeyDown}
                      className="px-3 py-1 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-xl font-bold focus:outline-none focus:border-purple-500"
                      autoFocus
                      disabled={saving}
                      data-testid="alias-edit-input"
                    />
                    <button
                      onClick={handleSaveAlias}
                      disabled={saving}
                      className="p-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                      title="Save"
                      data-testid="alias-save-button"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={saving}
                      className="p-1.5 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors"
                      title="Cancel"
                      data-testid="alias-cancel-button"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <h1 className="text-2xl font-bold text-white">{person.alias}</h1>
                    <button
                      onClick={handleStartEdit}
                      className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
                      title="Edit name"
                      data-testid="alias-edit-button"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
              
              {/* Edit error message */}
              {editError && (
                <p className="text-red-400 text-sm mb-2">{editError}</p>
              )}
              
              <p className="text-zinc-400">{RELATIONSHIP_LABELS[person.relationship] || person.relationship}</p>
              
              {/* Context notes / Synopsis */}
              {person.context_notes && (
                <p className="mt-3 text-zinc-300">{person.context_notes}</p>
              )}
              
              {/* Action buttons */}
              <div className="mt-4 flex flex-wrap gap-3">
                {/* Chat button */}
                <Link
                  to={`/dashboard/chat?person=${encodeURIComponent(person.alias)}`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                  data-testid="chat-about-person-button"
                >
                  <MessageSquare className="w-4 h-4" />
                  Chat about {displayName}
                </Link>
                
                {/* 1-1 Prep button */}
                <button
                  onClick={handleGeneratePrepNotes}
                  disabled={prepNotesLoading || !hasEnoughForPrep}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    hasEnoughForPrep 
                      ? 'bg-amber-600 hover:bg-amber-700 text-white' 
                      : 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
                  }`}
                  title={hasEnoughForPrep ? 'Generate 1-1 prep notes' : (prepNotesReason || 'More reflections needed')}
                  data-testid="generate-prep-notes-button"
                >
                  {prepNotesLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4" />
                  )}
                  {prepNotesLoading ? 'Generating...' : '1-1 Prep Notes'}
                </button>
              </div>
              
              {/* Not enough data message - show backend's reason */}
              {!hasEnoughForPrep && prepNotesReason && (
                <p className="mt-2 text-zinc-500 text-sm flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {prepNotesReason}
                </p>
              )}
              
              {/* Prep notes error */}
              {prepNotesError && (
                <p className="mt-2 text-red-400 text-sm flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {prepNotesError}
                </p>
              )}
              
              <div className="mt-4 flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2 text-zinc-400">
                  <MessageSquare className="w-4 h-4" />
                  {person.mention_count} mention{person.mention_count !== 1 ? 's' : ''}
                </div>
                {person.first_mentioned && (
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Calendar className="w-4 h-4" />
                    First mentioned {new Date(person.first_mentioned).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Prep Notes Panel - High contrast for readability */}
        {prepNotes && showPrepNotes && (
          <div className="bg-white dark:bg-zinc-100 border border-amber-400 rounded-xl p-6 mb-8 shadow-lg" data-testid="prep-notes-panel">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-amber-700 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                1-1 Prep Notes: {displayName}
              </h2>
              <div className="flex items-center gap-2">
                {/* Email button */}
                <button
                  onClick={handleEmailPrepNotes}
                  disabled={emailSending || emailSent}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    emailSent 
                      ? 'bg-green-600 text-white' 
                      : 'bg-amber-600 hover:bg-amber-700 text-white'
                  }`}
                  data-testid="email-prep-notes-button"
                >
                  {emailSending ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : emailSent ? (
                    <CheckCircle className="w-3 h-3" />
                  ) : (
                    <Mail className="w-3 h-3" />
                  )}
                  {emailSending ? 'Sending...' : emailSent ? 'Sent!' : 'Email to me'}
                </button>
                
                {/* Collapse button */}
                <button
                  onClick={() => setShowPrepNotes(false)}
                  className="p-1.5 text-zinc-500 hover:text-zinc-800 hover:bg-zinc-200 rounded-lg transition-colors"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {/* Parsed sections - High contrast text */}
            {prepNotes.sections && Object.keys(prepNotes.sections).length > 0 ? (
              <div className="space-y-5">
                {Object.entries(prepNotes.sections).map(([sectionName, items]) => (
                  <div key={sectionName}>
                    <h3 className="text-sm font-bold text-amber-700 mb-2 uppercase tracking-wide">{sectionName}</h3>
                    <ul className="space-y-2">
                      {items.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-800 text-sm leading-relaxed">
                          <span className="text-amber-600 mt-0.5 font-bold">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              /* Raw content fallback - High contrast */
              <div className="prose prose-sm max-w-none prose-headings:text-amber-700 prose-strong:text-gray-900 prose-p:text-gray-800 prose-li:text-gray-800">
                <ReactMarkdown>{prepNotes.raw_content}</ReactMarkdown>
              </div>
            )}
            
            <p className="mt-5 pt-4 border-t border-amber-200 text-xs text-gray-500">
              Based on {prepNotes.reflection_count} reflections • Generated {new Date(prepNotes.generated_at).toLocaleString()}
            </p>
          </div>
        )}
        
        {/* Collapsed prep notes indicator */}
        {prepNotes && !showPrepNotes && (
          <button
            onClick={() => setShowPrepNotes(true)}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-8 flex items-center justify-between hover:border-amber-500/30 transition-colors"
          >
            <span className="text-amber-400 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              1-1 Prep Notes available
            </span>
            <ChevronDown className="w-4 h-4 text-zinc-400" />
          </button>
        )}

        {/* Related reflections */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-zinc-400" />
            Related Reflections
            <span className="text-zinc-500 font-normal">({reflections.length})</span>
          </h2>

          {reflections.length === 0 ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center">
              <p className="text-zinc-400">No reflections found mentioning {person.alias}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reflections.map((reflection, index) => (
                <Link
                  key={index}
                  to={`/dashboard/reflections/${reflection.conversation_id}`}
                  className="block bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {reflection.situation_category && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${SITUATION_COLORS[reflection.situation_category] || SITUATION_COLORS.general}`}>
                          {reflection.situation_category.replace('_', ' ')}
                        </span>
                      )}
                      {reflection.emotional_tone && (
                        <span className="text-zinc-500 text-sm">
                          Feeling: {reflection.emotional_tone}
                        </span>
                      )}
                    </div>
                    <span className="text-zinc-500 text-sm">
                      {new Date(reflection.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default PersonDetailPage;
